import { useState } from "react";
import { ImageOff, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

/** The library passes the saved full-resolution URL, never a tile rendition. */
export function LibraryImagePreview({ url, name, adId }: {
  url: string | null;
  name: string;
  adId: string;
}) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");

  return (
    <Dialog open={open} onOpenChange={(next) => {
      if (next) setStatus(url ? "loading" : "error");
      setOpen(next);
    }}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="absolute inset-0 cursor-pointer rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
          aria-label={`Preview ${name}`}
          data-testid={`button-preview-ad-${adId}`}
        />
      </DialogTrigger>
      <DialogContent
        className="w-[calc(100vw-2rem)] max-w-[1200px] max-h-[calc(100dvh-2rem)] gap-3 p-4 sm:p-6"
        aria-describedby={undefined}
        data-testid="library-image-preview"
      >
        <DialogTitle className="min-w-0 truncate pr-8" title={name}>
          {name}
        </DialogTitle>
        <div className="relative flex h-[calc(100dvh-8rem)] max-h-[900px] min-h-0 items-center justify-center overflow-hidden rounded bg-muted/40">
          {status === "loading" && (
            <div role="status" className="absolute flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
              Loading image…
            </div>
          )}
          {status === "error" ? (
            <div role="alert" className="flex flex-col items-center gap-2 p-4 text-center text-muted-foreground">
              <ImageOff className="h-8 w-8" aria-hidden="true" />
              <p>This image is unavailable. Please close the preview and try again.</p>
            </div>
          ) : url && (
            <img
              src={url}
              alt={name}
              className={`h-full w-full object-contain ${status === "loading" ? "invisible" : ""}`}
              onLoad={() => setStatus("ready")}
              onError={() => setStatus("error")}
              data-testid="library-image-preview-image"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
